import { VariantProps, cva } from 'class-variance-authority';
import * as SelectPrimitive from '@radix-ui/react-select';
import * as React from 'react';

import { typographyVariants, Typography } from '../typography/typography';
import { ArrowDown, ArrowUp } from '../icons';
import { cn } from '../../utils';

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

export const selectTriggerVariants = cva(
  `flex px-3 py-2 w-full text-sm items-center justify-between rounded-md group
  outline outline-border-default bg-background
  ring-offset-background data-placeholder:text-weak
  hover:outline-border-hover
  data-[state=open]:ring-2 data-[state=open]:ring-border-sky data-[state=open]:outline-surface data-[state=open]:outline-1
  disabled:cursor-not-allowed [&>span]:line-clamp-1 disabled:bg-body disabled:data-placeholder:text-content-disabled disabled:text-content-disabled disabled:outline-none`,
  {
    variants: {
      size: {
        small: 'h-10',
        medium: 'h-12',
        large: 'h-14',
      },
      hasShadow: { true: 'shadow-sm disabled:shadow-none' },
      hasError: { true: 'outline-critical' },
      required: {
        true: "data-placeholder:[&_span]:after:content-['*'] data-placeholder:[&_span]:after:text-critical",
      },
    },
    defaultVariants: {
      hasShadow: false,
      size: 'small',
      required: false,
      hasError: false,
    },
  },
);

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> &
    VariantProps<typeof selectTriggerVariants>
>(({ className, children, size, hasShadow, required = false, hasError = false, ...props }, ref) => (
  <SelectPrimitive.Trigger
    className={cn(
      'gap-2',
      selectTriggerVariants({ size, hasShadow, required, hasError }),
      className,
    )}
    ref={ref}
    {...props}
  >
    <Typography variant="labelMediumBook" asChild>
      {children}
    </Typography>
    <SelectPrimitive.Icon asChild>
      <ArrowUp className="size-4 opacity-50 rotate-180" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    className={cn('flex cursor-default items-center justify-center py-1', className)}
    ref={ref}
    {...props}
  >
    <ArrowDown className="size-4" />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    className={cn('flex cursor-default items-center justify-center py-1', className)}
    ref={ref}
    {...props}
  >
    <ArrowDown className="size-4" />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & { viewport?: boolean }
>(({ className, children, position = 'popper', viewport = false, ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      className={cn(
        `relative z-50 max-h-96 min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-md border border-border-default h-full
          bg-background shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-
          data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2
          data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 `,
        position === 'popper' &&
          'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
        className,
      )}
      position={position}
      ref={ref}
      {...props}
    >
      {viewport ? (
        <SelectPrimitive.Viewport
          className={cn(
            'p-1',
            position === 'popper' &&
              'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]',
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
      ) : (
        <div
          className={cn(
            'overflow-y-auto [&::-webkit-scrollbar-track]:!bg-transparent [&::-webkit-scrollbar-thumb]:!bg-black/20 [&::-webkit-scrollbar-thumb]:!rounded',
            position === 'popper' &&
              'min-w-[var(--radix-select-trigger-width)] max-h-[var(--radix-popper-available-height)]',
          )}
        >
          {children}
        </div>
      )}
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    className={cn('py-1.5 pl-8 pr-2 text-sm font-semibold', className)}
    ref={ref}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    className={cn(
      'relative min-h-11 flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-3 pr-2 text-sm outline-none focus:bg-surface-hover data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[state=checked]:bg-surface-weak',
      className,
    )}
    ref={ref}
    {...props}
  >
    <SelectPrimitive.ItemText className={typographyVariants({ variant: 'labelMediumBook' })}>
      <span className="block w-full truncate">{children}</span>
    </SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    className={cn('-mx-1 my-1 h-px bg-disabled', className)}
    ref={ref}
    {...props}
  />
));

SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export const selectFloatingPlaceholderVariants = cva(
  'relative text-left z-10 text-weak transition-transform duration-300 origin-left group-disabled:text-content-disabled',
  {
    variants: {
      required: {
        true: "[&]:after:content-['*'] [&]:after:text-critical",
      },
      hasValue: {
        true: 'scale-75',
      },
    },
    defaultVariants: {
      required: false,
      hasValue: false,
    },
  },
);

interface SelectFloatingPlaceholderProps {
  children: React.ReactNode;
  placeholder?: string;
  floating?: boolean;
  required?: boolean;
  hasValue: boolean;
}

const SelectPlaceholder = ({
  required = false,
  floating = false,
  placeholder,
  children,
  hasValue,
}: SelectFloatingPlaceholderProps) => {
  return (
    <div className="text-left w-full">
      {(floating || !hasValue) && (
        <Typography
          className={cn('text-nowrap', selectFloatingPlaceholderVariants({ required, hasValue }))}
          variant="labelMediumBook"
        >
          {placeholder}
        </Typography>
      )}
      {hasValue && children}
    </div>
  );
};

export {
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectPlaceholder,
  SelectSeparator,
  SelectTrigger,
  SelectContent,
  SelectGroup,
  SelectValue,
  SelectLabel,
  SelectItem,
  Select,
};
